/**
 * 認証サービス層。
 *
 * プラットフォーム別のサインインフローを抽象化し、`AuthResult`（ID Token + プロバイダ）を返す。
 * 実際の Hub への反映（/auth/link, recoverAccount）は `accountLink.ts` が担う。
 *
 * 認証状態は `subscribeAuthState()` で購読可能。`useAuth()` が React hook として包む。
 *
 * 設計方針:
 *   - 各プロバイダ関数は ID Token を取得するところまでが責務。
 *     それ以降のアカウント連携（/auth/link, recoverAccount）と state 反映は呼び出し側で行う。
 *   - 失敗は throw する（サイレントフォールバック禁止）。ただしユーザーキャンセルは
 *     AuthCancelledError という専用エラーで識別する。
 *
 * 関連:
 *   - `src/services/accountLink.ts` — /auth/link + recoverAccount
 *   - `src/hooks/useAuth.ts` — React hook ラッパー
 *   - `src/components/AuthLoginModal.tsx` — UI
 */

import { getPlatformId, isCapacitorNative, isLineApp, registerAuthProbe } from '../utils/platform'
import { getFirebaseAuth } from './firebase'
import { getSafeLiffRedirectUri } from './liff'
import { AUTH_ERRORS, AUTH_PREF_KEYS, type AuthProvider } from '../config/auth'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface AuthProfileHint {
  displayName?: string
  email?: string
  pictureUrl?: string
}

export interface AuthResult {
  idToken: string
  provider: AuthProvider
  profile?: AuthProfileHint
}

export type AuthStatus = 'guest' | 'signed_in'

export interface AuthState {
  status: AuthStatus
  provider?: AuthProvider
  /** ID Token（プロバイダから取得した生トークン。Hub のセッショントークンとは別物）。 */
  idToken?: string
  /** サインインで得られるプロフィール情報（displayName 等、任意）。 */
  profile?: AuthProfileHint
}

export type AuthStateListener = (state: AuthState) => void
export type Unsubscribe = () => void

/**
 * 現在のプラットフォームで利用可能なサインインプロバイダの一覧を返す。
 *
 * - iOS: Apple Sign-In が必須（App Store ガイドライン 4.8）。Google も併記。
 * - Android: Google のみ。Apple Sign-In は技術的に動かない（Capacitor SocialLogin
 *   apple は iOS 専用）。
 * - LINE Mini App: LIFF 認証のみ。
 * - その他 Web: Google のみ（将来 web_user 化したとき）。
 *
 * AuthLoginModal / SettingsModal AccountTab の両方から参照することで、
 * プロバイダ表示の整合性を保つ。
 */
export function getAvailableLoginProviders(): AuthProvider[] {
  const platform = getPlatformId()
  if (platform === 'ios') return ['apple', 'google']
  if (platform === 'android') return ['google']
  if (platform === 'line_mini') return ['line']
  return ['google']
}

/** ユーザーがモーダル等でキャンセルした場合の識別用エラー。 */
export class AuthCancelledError extends Error {
  constructor(message = 'sign-in cancelled by user') {
    super(message)
    this.name = 'AuthCancelledError'
  }
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let currentState: AuthState = { status: 'guest' }
const listeners = new Set<AuthStateListener>()
let socialLoginInitPromise: Promise<void> | null = null

// Web で認証済みかどうかを platform.ts から参照できるように登録する。
// （services/auth を直接 import すると circular import になるため、pull 方式ではなく push 方式にする）
// currentState 宣言後に登録する（TDZ 回避）。
registerAuthProbe(() => currentState.status === 'signed_in')

function setState(next: AuthState): void {
  currentState = next
  for (const l of listeners) {
    try {
      l(currentState)
    } catch (err) {
      console.warn('[auth] listener error:', err)
    }
  }
}

function isAuthProvider(value: string | null): value is AuthProvider {
  return value === 'google' || value === 'apple' || value === 'line'
}

function readEnv(name: string): string | undefined {
  const value = import.meta.env[name] as string | undefined
  const trimmed = value?.trim()
  return trimmed || undefined
}

export function getAuthState(): AuthState {
  return currentState
}

export function subscribeAuthState(listener: AuthStateListener): Unsubscribe {
  listeners.add(listener)
  // 現在値を即時通知
  try {
    listener(currentState)
  } catch (err) {
    console.warn('[auth] listener error:', err)
  }
  return () => {
    listeners.delete(listener)
  }
}

// ---------------------------------------------------------------------------
// Helpers: Capacitor Preferences (lazy import for web builds)
// ---------------------------------------------------------------------------

async function prefGet(key: string): Promise<string | null> {
  if (!isCapacitorNative()) return null
  const { Preferences } = await import('@capacitor/preferences')
  const { value } = await Preferences.get({ key })
  return value ?? null
}

async function prefSet(key: string, value: string): Promise<void> {
  if (!isCapacitorNative()) return
  const { Preferences } = await import('@capacitor/preferences')
  await Preferences.set({ key, value })
}

async function prefRemove(key: string): Promise<void> {
  if (!isCapacitorNative()) return
  const { Preferences } = await import('@capacitor/preferences')
  await Preferences.remove({ key })
}

async function clearStoredAuthHint(): Promise<void> {
  await prefRemove(AUTH_PREF_KEYS.lastProvider)
  await prefRemove(AUTH_PREF_KEYS.lastStatus)
}

// ---------------------------------------------------------------------------
// Native SocialLogin initialization
// ---------------------------------------------------------------------------

/**
 * Capacitor Social Login を現在のプラットフォーム向けに初期化する。
 *
 * App 起動時にも呼ぶが、ログイン直前にも同じ Promise を await することで、
 * 初期化タイミングの競合や起動時の一時失敗をログイン処理側で再試行できるようにする。
 */
export async function initializeSocialLoginProviders(): Promise<void> {
  if (!isCapacitorNative()) return
  if (socialLoginInitPromise) return socialLoginInitPromise

  socialLoginInitPromise = (async () => {
    const { Capacitor } = await import('@capacitor/core')
    const { SocialLogin } = await import('@capgo/capacitor-social-login')
    const platform = Capacitor.getPlatform()
    const googleWebClientId = readEnv('VITE_GOOGLE_WEB_CLIENT_ID')
    const googleIosClientId = readEnv('VITE_GOOGLE_IOS_CLIENT_ID')

    let googleConfig: Record<string, unknown> = {}
    if (platform === 'ios') {
      if (googleIosClientId && googleWebClientId) {
        googleConfig = {
          google: {
            iOSClientId: googleIosClientId,
            iOSServerClientId: googleWebClientId,
            mode: 'online',
          },
        }
      }
    } else if (googleWebClientId) {
      googleConfig = { google: { webClientId: googleWebClientId, mode: 'online' } }
    } else {
      throw new Error(`Google Sign-In client ID is not configured for ${platform}`)
    }

    const providers = platform === 'ios' ? { apple: {}, ...googleConfig } : googleConfig
    await SocialLogin.initialize(providers as never)
    console.log(
      `[auth] SocialLogin initialized for ${platform} (iosClientId=${googleIosClientId ? 'set' : 'unset'}, webClientId=${googleWebClientId ? 'set' : 'unset'})`,
    )
  })().catch((err) => {
    socialLoginInitPromise = null
    throw err
  })

  return socialLoginInitPromise
}

// ---------------------------------------------------------------------------
// Google Sign-In
// ---------------------------------------------------------------------------

/**
 * Google でサインインする。
 *
 * プラットフォーム分岐:
 *   - Capacitor ネイティブ (iOS/Android) → @capgo/capacitor-social-login
 *   - Web ブラウザ → Firebase Auth popup
 *
 * 返り値は `AuthResult`。失敗時は throw（ユーザーキャンセルは AuthCancelledError）。
 */
export async function signInWithGoogle(): Promise<AuthResult> {
  if (isCapacitorNative()) {
    return signInWithGoogleNative()
  }
  return signInWithGoogleWeb()
}

async function signInWithGoogleNative(): Promise<AuthResult> {
  let SocialLogin: typeof import('@capgo/capacitor-social-login').SocialLogin
  try {
    await initializeSocialLoginProviders()
    const mod = await import('@capgo/capacitor-social-login')
    SocialLogin = mod.SocialLogin
  } catch (err) {
    throw new Error(`${AUTH_ERRORS.socialLoginMissing}: ${String(err)}`)
  }

  const loginResponse = await SocialLogin.login({ provider: 'google', options: {} })
  const result = loginResponse?.result as
    | { responseType?: string; idToken?: string; profile?: { name?: string; email?: string; imageUrl?: string } }
    | undefined

  if (!result) {
    throw new AuthCancelledError()
  }
  if (result.responseType && result.responseType !== 'online') {
    // 'offline' など、ID Token が取れないレスポンスはサインインキャンセル扱い
    throw new AuthCancelledError(`unexpected responseType=${result.responseType}`)
  }
  if (!result.idToken) {
    throw new Error(AUTH_ERRORS.tokenMissing)
  }

  const authResult: AuthResult = {
    idToken: result.idToken,
    provider: 'google',
    profile: {
      displayName: result.profile?.name,
      email: result.profile?.email,
      pictureUrl: result.profile?.imageUrl,
    },
  }
  return authResult
}

async function signInWithGoogleWeb(): Promise<AuthResult> {
  const auth = getFirebaseAuth()
  if (!auth) throw new Error(AUTH_ERRORS.firebaseNotConfigured)

  const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth')
  const provider = new GoogleAuthProvider()

  let userCredential
  try {
    userCredential = await signInWithPopup(auth, provider)
  } catch (err: unknown) {
    // Firebase のエラー code は code フィールドに入っている
    const code = (err as { code?: string } | undefined)?.code
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      throw new AuthCancelledError()
    }
    throw err
  }

  const idToken = await userCredential.user.getIdToken()
  if (!idToken) throw new Error(AUTH_ERRORS.tokenMissing)

  const authResult: AuthResult = {
    idToken,
    provider: 'google',
    profile: {
      displayName: userCredential.user.displayName ?? undefined,
      email: userCredential.user.email ?? undefined,
      pictureUrl: userCredential.user.photoURL ?? undefined,
    },
  }
  return authResult
}

// ---------------------------------------------------------------------------
// Apple Sign-In (iOS Capacitor only)
// ---------------------------------------------------------------------------

/**
 * Apple でサインインする。iOS Capacitor でのみ動作。
 * Android / Web では AUTH_ERRORS.unsupportedMessage を throw する。
 */
export async function signInWithApple(): Promise<AuthResult> {
  const platform = getPlatformId()
  if (platform !== 'ios') {
    throw new Error(`${AUTH_ERRORS.notImplemented} (platform=${platform})`)
  }

  let SocialLogin: typeof import('@capgo/capacitor-social-login').SocialLogin
  try {
    await initializeSocialLoginProviders()
    const mod = await import('@capgo/capacitor-social-login')
    SocialLogin = mod.SocialLogin
  } catch (err) {
    throw new Error(`${AUTH_ERRORS.socialLoginMissing}: ${String(err)}`)
  }

  const loginResponse = await SocialLogin.login({ provider: 'apple', options: {} })
  const result = loginResponse?.result as
    | { idToken?: string; profile?: { givenName?: string; familyName?: string; email?: string } }
    | undefined

  if (!result) throw new AuthCancelledError()
  if (!result.idToken) throw new Error(AUTH_ERRORS.tokenMissing)

  const fullName = [result.profile?.givenName, result.profile?.familyName].filter(Boolean).join(' ')
  const authResult: AuthResult = {
    idToken: result.idToken,
    provider: 'apple',
    profile: {
      displayName: fullName || undefined,
      email: result.profile?.email,
    },
  }
  return authResult
}

// ---------------------------------------------------------------------------
// LINE Sign-In (LIFF / Mini App)
// ---------------------------------------------------------------------------

/**
 * LINE でサインインする（LIFF 環境のみ）。
 *
 * T12 段階ではスケルトン実装:
 *   - LIFF SDK がロードされており、`liff.isLoggedIn()` が true なら `liff.getIDToken()` を返す。
 *   - まだログインしていなければ `liff.login()` でリダイレクト（throw で抜ける）。
 *   - LIFF 初期化自体は現状の nawabari では行っていないため、呼び出し側で LIFF を初期化して
 *     からこの関数を呼ぶこと。T16（LINE フレンド招待機能）で本実装を詰める。
 */
export async function signInWithLINE(): Promise<AuthResult> {
  if (!isLineApp()) {
    throw new Error(`${AUTH_ERRORS.notImplemented} (not inside LIFF)`)
  }

  const w = window as unknown as {
    liff?: {
      isLoggedIn?: () => boolean
      login?: (params?: { redirectUri?: string }) => void
      getIDToken?: () => string | null
      getProfile?: () => Promise<{ displayName?: string; pictureUrl?: string }>
    }
  }
  const liff = w.liff
  if (!liff) throw new Error(AUTH_ERRORS.liffNotInitialized)

  if (!liff.isLoggedIn?.()) {
    // redirectUri は origin + pathname のみに絞る（Open Redirect 対策）。
    // 同ファイルの他箇所 / sdk.ts / ymgFetch では既に getSafeLiffRedirectUri()
    // を使用しており、本箇所も統一する。
    liff.login?.({ redirectUri: getSafeLiffRedirectUri() })
    // login() はリダイレクトを起こすので以降に到達しない想定。念のため throw で抜ける。
    throw new AuthCancelledError('LINE login redirect triggered')
  }

  const idToken = liff.getIDToken?.() ?? null
  if (!idToken) throw new Error(AUTH_ERRORS.tokenMissing)

  let profile: { displayName?: string; pictureUrl?: string } | undefined
  try {
    profile = await liff.getProfile?.()
  } catch (err) {
    console.warn('[auth] liff.getProfile failed:', err)
  }

  const authResult: AuthResult = {
    idToken,
    provider: 'line',
    profile: {
      displayName: profile?.displayName,
      pictureUrl: profile?.pictureUrl,
    },
  }
  return authResult
}

// ---------------------------------------------------------------------------
// Sign-out
// ---------------------------------------------------------------------------

/**
 * サインアウトする。
 *
 * - Firebase Auth（Web）は signOut() を呼ぶ
 * - Capacitor Social Login はセッションを明示終了
 * - Capacitor Preferences に保存した session / provider を削除
 * - 状態を 'guest' に戻す
 *
 * Hub 側のセッション token 無効化は呼ばない（Hub 側は期限切れで自然失効する想定）。
 * 強制的にサーバー側セッションを切りたい場合は T12.5 以降の要件で別途対応する。
 */
export async function signOut(): Promise<void> {
  if (isCapacitorNative()) {
    try {
      const { resetYmgSdkSession } = await import('./sdk')
      await resetYmgSdkSession({ revoke: true })
    } catch (err) {
      console.warn('[auth] SDK session reset failed:', err)
    }
  }

  // Firebase Web
  if (!isCapacitorNative()) {
    const auth = getFirebaseAuth()
    if (auth) {
      try {
        const { signOut: fbSignOut } = await import('firebase/auth')
        await fbSignOut(auth)
      } catch (err) {
        // fallback:ok — Firebase signOut はベストエフォート。
        // ID Token は短命（1時間）でクライアント側で破棄するだけで実害は限定的。
        // 例外で signOut を中断すると Capacitor 経路や状態リセットも止まるため warn のみ。
        console.warn('[auth] firebase signOut failed:', err)
      }
    }
  }

  // Capacitor Social Login
  if (isCapacitorNative()) {
    try {
      const { SocialLogin } = await import('@capgo/capacitor-social-login')
      // provider は logout 時点の state を使う（未ログインなら skip）
      const provider = currentState.provider
      if (provider === 'google' || provider === 'apple') {
        await SocialLogin.logout({ provider })
      }
    } catch (err) {
      // fallback:ok — SocialLogin の OS 連携は再起動などで揺らぐが、
      // アプリ内 session（次の prefRemove 群）を確実にクリアできれば
      // ユーザー体感としてのサインアウトは成立する。
      console.warn('[auth] social logout failed:', err)
    }

    // 保存済みセッションを削除（次回起動時にゲストトークンで bootstrap する）
    await prefRemove(AUTH_PREF_KEYS.session)
    await prefRemove(AUTH_PREF_KEYS.refresh)
    await prefRemove(AUTH_PREF_KEYS.sessionExpiresAt)
    await prefRemove(AUTH_PREF_KEYS.refreshExpiresAt)
    await prefRemove(AUTH_PREF_KEYS.sessionId)
    await prefRemove(AUTH_PREF_KEYS.lastProvider)
    await prefRemove(AUTH_PREF_KEYS.lastStatus)
  }

  setState({ status: 'guest' })
}

// ---------------------------------------------------------------------------
// Internal: state update helper
// ---------------------------------------------------------------------------

export async function commitAuthResult(result: AuthResult): Promise<void> {
  setState({
    status: 'signed_in',
    provider: result.provider,
    idToken: result.idToken,
    profile: result.profile,
  })
  // 再起動時に同じ provider の状態を復元したいので Capacitor のときだけ Preferences に残す
  await prefSet(AUTH_PREF_KEYS.lastProvider, result.provider)
  await prefSet(AUTH_PREF_KEYS.lastStatus, 'signed_in')
}

/**
 * 起動時に、以前サインイン済みだったかを Preferences から読み取り、
 * Native の YMG session token が残っている場合だけ state を復元する。
 *
 * ID Token は保存しない（短命かつプロバイダの生トークンのため）。Native の API 通信は
 * `AUTH_PREF_KEYS.session` を Bearer に使うので、ここでは UI / authProbe 用に
 * provider と signed_in 状態だけを復元する。
 */
export async function restoreAuthHint(): Promise<void> {
  if (!isCapacitorNative()) return
  const status = await prefGet(AUTH_PREF_KEYS.lastStatus)
  if (status !== 'signed_in') return
  const provider = await prefGet(AUTH_PREF_KEYS.lastProvider)
  if (!isAuthProvider(provider)) {
    await clearStoredAuthHint()
    setState({ status: 'guest' })
    return
  }

  const sessionToken = await prefGet(AUTH_PREF_KEYS.session)
  if (!sessionToken) {
    await clearStoredAuthHint()
    setState({ status: 'guest' })
    return
  }

  setState({ status: 'signed_in', provider })
}
